/**
 * Top-right settings menu — conversation font size (persisted on user profile).
 */

import { useEffect, useRef, useState } from 'react';
import { Check, Settings, Type } from 'lucide-react';
import type { ConversationFontSize } from '../api.js';

const FONT_OPTIONS: Array<{
  value: ConversationFontSize;
  label: string;
  sampleClass: string;
}> = [
  { value: 'sm', label: 'Small', sampleClass: 'text-xs' },
  { value: 'md', label: 'Medium', sampleClass: 'text-sm' },
  { value: 'lg', label: 'Large', sampleClass: 'text-base' },
  { value: 'xl', label: 'Extra large', sampleClass: 'text-lg' },
];

interface SettingsMenuProps {
  fontSize: ConversationFontSize;
  saving: boolean;
  disabled?: boolean;
  onFontSizeChange: (size: ConversationFontSize) => void;
}

export function SettingsMenu({
  fontSize,
  saving,
  disabled,
  onFontSizeChange,
}: SettingsMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        className="inline-flex items-center justify-center rounded-lg p-1.5 text-stone-500 transition hover:bg-stone-100 hover:text-stone-800 disabled:opacity-40"
        title="Settings"
        aria-label="Settings"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Settings className="h-4 w-4" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1 w-56 overflow-hidden rounded-xl border border-stone-200 bg-canvas-elevated py-1 shadow-soft"
        >
          <div className="flex items-center gap-1.5 border-b border-stone-100 px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-stone-400">
            <Type className="h-3 w-3" />
            Conversation font
            {saving && (
              <span className="ml-auto normal-case tracking-normal text-stone-400">
                Saving…
              </span>
            )}
          </div>
          <ul className="py-1">
            {FONT_OPTIONS.map((opt) => {
              const selected = opt.value === fontSize;
              return (
                <li key={opt.value}>
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={selected}
                    disabled={saving}
                    onClick={() => {
                      if (opt.value === fontSize) {
                        setOpen(false);
                        return;
                      }
                      onFontSizeChange(opt.value);
                      setOpen(false);
                    }}
                    className={
                      'flex w-full items-center gap-2 px-3 py-2 text-left transition hover:bg-stone-50 disabled:opacity-50 ' +
                      (selected ? 'bg-stone-50' : '')
                    }
                  >
                    <span className={`min-w-0 flex-1 font-medium text-stone-800 ${opt.sampleClass}`}>
                      {opt.label}
                    </span>
                    {selected && (
                      <Check className="h-3.5 w-3.5 shrink-0 text-stone-900" aria-hidden />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
